import React from 'react';
import { useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../utils/locale';
import { gql, useQuery } from '@apollo/client';
import { Handshake as HandshakeIcon, ArrowRight as ArrowRightIcon } from 'lucide-react';

const GET_DEAL_ROOM_METRICS = gql`
  query GetDealRoomMetricsCard {
    getCurrentUser {
      id
      user_name
      can_create_meeting_room
      company { id name deal_room_branded_name deal_room_branded_name_fr deal_room_single_word deal_room_single_word_fr }
      inventory_user {
        id
        deals { id number_of_invites number_of_proposals number_of_ndas number_of_negotiations matches_count deal_state creation_date }
      }
    }
  }
`;

// Funnel visualization component - fixed widths for consistent funnel shape
const DealFunnel = ({ matches = 0, invites = 0, proposals = 0, ndas = 0, negotiations = 0, intl }) => {
  const stages = [
    { label: intl.formatMessage({ id: 'succession.launchpad.funnel_matches' }), value: matches, width: '100%' },
    { label: intl.formatMessage({ id: 'succession.launchpad.funnel_invites' }), value: invites, width: '85%' },
    { label: intl.formatMessage({ id: 'succession.launchpad.funnel_proposals' }), value: proposals, width: '70%' },
    { label: intl.formatMessage({ id: 'succession.launchpad.funnel_ndas' }), value: ndas, width: '55%' },
    { label: intl.formatMessage({ id: 'succession.launchpad.funnel_negotiating' }), value: negotiations, width: '40%' },
  ];

  return (
    <div className="space-y-1">
      {stages.map((stage, index) => (
        <div key={stage.label} className="flex items-center justify-center">
          <div
            className="text-white py-4 px-6 text-center font-medium"
            style={{
              width: stage.width,
              background: 'linear-gradient(to right, var(--succession-primary), var(--succession-primary-hover, var(--succession-primary)))',
              clipPath:
                index === 0
                  ? 'polygon(5% 0%, 95% 0%, 90% 100%, 10% 100%)'
                  : index === stages.length - 1
                    ? 'polygon(10% 0%, 90% 0%, 85% 100%, 15% 100%)'
                    : 'polygon(10% 0%, 90% 0%, 85% 100%, 15% 100%)',
            }}
          >
            {stage.label}: {stage.value}
          </div>
        </div>
      ))}
    </div>
  );
};

// Format time on market
const formatTimeOnMarket = (dateStr, intl) => {
  if (!dateStr) return null;
  try {
    const start = new Date(dateStr);
    const now = new Date();
    const ms = Math.max(0, now - start);
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30);
    if (months < 1) return intl.formatMessage({ id: 'succession.launchpad.time_days' }, { count: days });
    return intl.formatMessage({ id: 'succession.launchpad.time_months' }, { count: months });
  } catch (_) {
    return null;
  }
};

const DealRoomMetricsCard = ({ className = "", showHeaderIcon = true }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_DEAL_ROOM_METRICS, { fetchPolicy: 'cache-first' });

  if (loading) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-succession-bg-secondary animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-succession-bg-secondary rounded w-24 animate-pulse" />
            <div className="h-4 bg-succession-bg-secondary rounded w-40 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="text-sm text-succession-error">{intl.formatMessage({ id: 'succession.launchpad.unable_to_load_deal_room' })}</div>
      </div>
    );
  }

  const cu = data?.getCurrentUser || {};
  const deals = cu?.inventory_user?.deals || [];
  const activeDeal = deals.find((d) => String(d.deal_state).toLowerCase() !== 'closed') || deals[0];
  const hasDealRoom = Boolean(activeDeal);
  const canCreate = Boolean(cu?.can_create_meeting_room);
  const word = getDealRoomBrandedName(cu?.company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });

  // Calculate metrics for active state
  const matches = Number(activeDeal?.matches_count || 0);
  const invites = Number(activeDeal?.number_of_invites || 0);
  const proposals = Number(activeDeal?.number_of_proposals || 0);
  const ndas = Number(activeDeal?.number_of_ndas || 0);
  const negotiations = Number(activeDeal?.number_of_negotiations || 0);
  const totalInquiries = invites + proposals + ndas + negotiations;
  const timeOnMarket = formatTimeOnMarket(activeDeal?.creation_date, intl);

  // Empty state (no deal room)
  if (!hasDealRoom) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 hover:shadow-succession-lg transition-shadow h-full ${className}`}>
        <div className="space-y-4 h-full flex flex-col">
          {/* Header */}
          <div className={`flex items-start ${showHeaderIcon ? 'gap-4' : 'gap-0'}`}>
            {showHeaderIcon && (
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-primary/10">
                <HandshakeIcon className="w-6 h-6 text-succession-primary" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{word}</h3>
              <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
                {intl.formatMessage({ id: 'succession.launchpad.deal_room_subtitle_empty' })}
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-succession-text-secondary leading-relaxed flex-1">
            {intl.formatMessage({ id: 'succession.launchpad.deal_room_description' }, { word: word.toLowerCase() })}
          </p>

          {/* Action */}
          {canCreate ? (
            <a
              href="/succession/deals"
              className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium border border-succession-border rounded-md bg-succession-bg-secondary text-succession-text-primary hover:bg-succession-hover-bg transition-colors group"
            >
              {intl.formatMessage({ id: 'succession.launchpad.deal_room_create' }, { word })}
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
          ) : (
            <div className="text-center py-3 px-4 bg-succession-bg-secondary rounded-md">
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.launchpad.contact_advisor_deal_room' }, { word: word.toLowerCase() })}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active state (has deal room)
  return (
    <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 hover:shadow-succession-lg transition-shadow h-full ${className}`}>
      {/* Header */}
      <div className="mb-6">
        {showHeaderIcon ? (
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-primary/10">
              <HandshakeIcon className="w-6 h-6 text-succession-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{word}</h3>
              <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
                {intl.formatMessage({ id: 'succession.launchpad.deal_room_subtitle_active' }, { word: word.toLowerCase() })}
              </p>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{word}</h3>
            <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
              {intl.formatMessage({ id: 'succession.launchpad.deal_room_subtitle_active' }, { word: word.toLowerCase() })}
            </p>
          </>
        )}
      </div>

      {/* Funnel Visualization */}
      <div className="mb-8">
        <DealFunnel
          matches={matches}
          invites={invites}
          proposals={proposals}
          ndas={ndas}
          negotiations={negotiations}
          intl={intl}
        />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b border-succession-border">
        <div>
          <div className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.launchpad.total_inquiries' })}</div>
          <div className="text-2xl font-bold text-succession-text-primary">{totalInquiries}</div>
        </div>
        <div>
          <div className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.launchpad.time_on_market' })}</div>
          <div className="text-2xl font-bold text-succession-text-primary">{timeOnMarket || '—'}</div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end">
        <a
          href="/succession/deals"
          className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group p-0"
        >
          {intl.formatMessage({ id: 'succession.launchpad.view_deal_room' }, { word })}
          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  );
};

export default DealRoomMetricsCard;
